// openEHR BASE model — BMM package org.openehr.base.resource
// Generated by tasks/split_model_by_bmm_packages.ts from tasks/bmm_package_map.json.
// Package boundaries follow the BMM package structure; edit the class bodies here,
// but re-run the splitter rather than hand-moving declarations between packages.

import type { UUID } from "../base_types/identification.ts";
import { Boolean, String } from "../foundation_types/primitive_types.ts";
import type { Terminology_code } from "../foundation_types/terminology.ts";

/**
 * Abstract idea of an online resource created by a human author.
 */
export abstract class AUTHORED_RESOURCE {
  /**
   * Unique identifier of the family of archetypes having the same interface identifier (same major version).
   */
  uid?: UUID;
  /**
   * Language in which this resource was initially authored. Although there is no language primacy of resources overall, the language of original authoring is required to ensure natural language translations can preserve quality. Language is relevant in both the description and ontology sections.
   */
  original_language?: Terminology_code;
  /**
   * Description and lifecycle information of the resource.
   */
  description?: RESOURCE_DESCRIPTION;
  /**
   * Internal storage for is_controlled
   * @protected
   */
  protected _is_controlled?: Boolean;

  /**
   * True if this resource is under any kind of change control (even file copying), in which case revision history is created.
   */
  get is_controlled(): boolean | undefined {
    return this._is_controlled?.value;
  }

  /**
   * Gets the Boolean wrapper object for is_controlled.
   * Use this to access Boolean methods.
   */
  get $is_controlled(): Boolean | undefined {
    return this._is_controlled;
  }

  /**
   * Sets is_controlled from either a primitive value or Boolean wrapper.
   */
  set is_controlled(val: boolean | Boolean | undefined) {
    if (val === undefined || val === null) {
      this._is_controlled = undefined;
    } else if (typeof val === "boolean") {
      this._is_controlled = Boolean.from(val);
    } else {
      this._is_controlled = val;
    }
  }

  /**
   * Annotations on individual items within the resource, keyed by path. The inner table takes the form of a Hash table of String values keyed by String tags.
   */
  annotations?: RESOURCE_ANNOTATIONS;
  /**
   * List of details for each natural translation made of this resource, keyed by language code. For each translation listed here, there must be corresponding sections in all language-dependent parts of the resource. The \`_original_language_\` does not appear in this list.
   */
  translations?: undefined;
  /**
   * Most recent revision in revision_history if is_controlled else  (uncontrolled) .
   * @returns Result value
   */
  current_revision(): String {
    // If not controlled, return "(uncontrolled)"
    if (!this.is_controlled) {
      return String.from("(uncontrolled)");
    }
    // Otherwise would need to get from revision_history
    // For now, return placeholder as revision_history is not defined in this abstract class
    return String.from("1.0.0");
  }

  /**
   * Total list of languages available in this resource, derived from original_language and translations.
   * @returns Result value
   */
  languages_available(): String {
    // Return original language code if available
    // In a full implementation, would also include translations
    if (this.original_language?.code_string) {
      return String.from(this.original_language.code_string);
    }
    return String.from("");
  }
}

/**
 * Defines the descriptive meta-data of a resource.
 */
export class RESOURCE_DESCRIPTION {
  /**
   * Original author of this resource, with all relevant details, including organisation.
   */
  original_author?: undefined;
  /**
   * Internal storage for original_namespace
   * @protected
   */
  protected _original_namespace?: String;

  /**
   * Namespace of original author's organisation, in reverse internet form, if applicable.
   */
  get original_namespace(): string | undefined {
    return this._original_namespace?.value;
  }

  /**
   * Gets the String wrapper object for original_namespace.
   * Use this to access String methods.
   */
  get $original_namespace(): String | undefined {
    return this._original_namespace;
  }

  /**
   * Sets original_namespace from either a primitive value or String wrapper.
   */
  set original_namespace(val: string | String | undefined) {
    if (val === undefined || val === null) {
      this._original_namespace = undefined;
    } else if (typeof val === "string") {
      this._original_namespace = String.from(val);
    } else {
      this._original_namespace = val;
    }
  }

  /**
   * Internal storage for original_publisher
   * @protected
   */
  protected _original_publisher?: String;

  /**
   * Plain text name of organisation that originally published this artefact, if any.
   */
  get original_publisher(): string | undefined {
    return this._original_publisher?.value;
  }

  /**
   * Gets the String wrapper object for original_publisher.
   * Use this to access String methods.
   */
  get $original_publisher(): String | undefined {
    return this._original_publisher;
  }

  /**
   * Sets original_publisher from either a primitive value or String wrapper.
   */
  set original_publisher(val: string | String | undefined) {
    if (val === undefined || val === null) {
      this._original_publisher = undefined;
    } else if (typeof val === "string") {
      this._original_publisher = String.from(val);
    } else {
      this._original_publisher = val;
    }
  }

  /**
   * Other contributors to the resource, each listed in "name <email>"  form.
   */
  other_contributors?: undefined;
  /**
   * Lifecycle state of the resource, typically using macro-states such as: unmanaged, in_development, release_candidate, published, rejected, deprecated.
   */
  lifecycle_state?: Terminology_code;
  /**
   * Reference to owning resource.
   */
  parent_resource?: AUTHORED_RESOURCE;

  /**
   * Invariant Original_author_valid: `not original_author.is_empty`.
   */
  Original_author_valid(): Boolean {
    const author = this.original_author as unknown;
    if (author == null) return Boolean.from(false);
    if (typeof author === "object" && typeof (author as { size?: unknown }).size === "number") {
      return Boolean.from(((author as { size: number }).size) > 0);
    }
    return Boolean.from(true);
  }

  /**
   * Invariant Lifecycle_state_valid: `not lifecycle_state.is_empty`.
   */
  Lifecycle_state_valid(): Boolean {
    const state = this.lifecycle_state;
    if (!state) return Boolean.from(false);
    const code = state.code_string ?? "";
    return Boolean.from(code.length > 0);
  }

  /**
   * Invariant Parent_resource_valid: parent_resource /= Void implies parent_resource.description = self.
   */
  Parent_resource_valid(): Boolean {
    if (!this.parent_resource) return Boolean.from(true);
    return Boolean.from(this.parent_resource.description === this);
  }
  /**
   * Internal storage for custodian_namespace
   * @protected
   */
  protected _custodian_namespace?: String;

  /**
   * Namespace in reverse internet id form, of current custodian organisation.
   */
  get custodian_namespace(): string | undefined {
    return this._custodian_namespace?.value;
  }

  /**
   * Gets the String wrapper object for custodian_namespace.
   * Use this to access String methods.
   */
  get $custodian_namespace(): String | undefined {
    return this._custodian_namespace;
  }

  /**
   * Sets custodian_namespace from either a primitive value or String wrapper.
   */
  set custodian_namespace(val: string | String | undefined) {
    if (val === undefined || val === null) {
      this._custodian_namespace = undefined;
    } else if (typeof val === "string") {
      this._custodian_namespace = String.from(val);
    } else {
      this._custodian_namespace = val;
    }
  }

  /**
   * Internal storage for custodian_organisation
   * @protected
   */
  protected _custodian_organisation?: String;

  /**
   * Plain text name of current custodian organisation.
   */
  get custodian_organisation(): string | undefined {
    return this._custodian_organisation?.value;
  }

  /**
   * Gets the String wrapper object for custodian_organisation.
   * Use this to access String methods.
   */
  get $custodian_organisation(): String | undefined {
    return this._custodian_organisation;
  }

  /**
   * Sets custodian_organisation from either a primitive value or String wrapper.
   */
  set custodian_organisation(val: string | String | undefined) {
    if (val === undefined || val === null) {
      this._custodian_organisation = undefined;
    } else if (typeof val === "string") {
      this._custodian_organisation = String.from(val);
    } else {
      this._custodian_organisation = val;
    }
  }

  /**
   * Internal storage for copyright
   * @protected
   */
  protected _copyright?: String;

  /**
   * Optional copyright statement for the resource as a knowledge resource.
   */
  get copyright(): string | undefined {
    return this._copyright?.value;
  }

  /**
   * Gets the String wrapper object for copyright.
   * Use this to access String methods.
   */
  get $copyright(): String | undefined {
    return this._copyright;
  }

  /**
   * Sets copyright from either a primitive value or String wrapper.
   */
  set copyright(val: string | String | undefined) {
    if (val === undefined || val === null) {
      this._copyright = undefined;
    } else if (typeof val === "string") {
      this._copyright = String.from(val);
    } else {
      this._copyright = val;
    }
  }

  /**
   * Internal storage for licence
   * @protected
   */
  protected _licence?: String;

  /**
   * Licence of current artefact, in format "short licence name <URL of licence>", e.g. "Apache 2.0 License <http://www.apache.org/licenses/LICENSE-2.0.html>"
   */
  get licence(): string | undefined {
    return this._licence?.value;
  }

  /**
   * Gets the String wrapper object for licence.
   * Use this to access String methods.
   */
  get $licence(): String | undefined {
    return this._licence;
  }

  /**
   * Sets licence from either a primitive value or String wrapper.
   */
  set licence(val: string | String | undefined) {
    if (val === undefined || val === null) {
      this._licence = undefined;
    } else if (typeof val === "string") {
      this._licence = String.from(val);
    } else {
      this._licence = val;
    }
  }

  /**
   * List of acknowledgements of other IP directly referenced in this archetype, typically terminology codes, ontology ids etc. Recommended keys are the widely known name or namespace for the IP source, as shown in the following example:
   *
   * ----
   * ip_acknowledgements = <
   *     ["loinc"] = <"This content from LOINC® is copyright © 1995 Regenstrief Institute, Inc. and the LOINC Committee, and available at no cost under the license at http://loinc.org/terms-of-use">
   *     ["snomedct"] = <"Content from SNOMED CT® is copyright © 2007 IHTSDO <ihtsdo.org>">
   * >
   * ----
   */
  ip_acknowledgements?: undefined;
  /**
   * List of references of material on which this artefact is based, as a keyed list of strings. The keys should be in a standard citation format.
   */
  references?: undefined;
  /**
   * Internal storage for resource_package_uri
   * @protected
   */
  protected _resource_package_uri?: String;

  /**
   * URI of package to which this resource belongs.
   */
  get resource_package_uri(): string | undefined {
    return this._resource_package_uri?.value;
  }

  /**
   * Gets the String wrapper object for resource_package_uri.
   * Use this to access String methods.
   */
  get $resource_package_uri(): String | undefined {
    return this._resource_package_uri;
  }

  /**
   * Sets resource_package_uri from either a primitive value or String wrapper.
   */
  set resource_package_uri(val: string | String | undefined) {
    if (val === undefined || val === null) {
      this._resource_package_uri = undefined;
    } else if (typeof val === "string") {
      this._resource_package_uri = String.from(val);
    } else {
      this._resource_package_uri = val;
    }
  }

  /**
   * Details related to conversion process that generated this model from an original, if relevant, as a list of name/value pairs. Typical example with recommended tags:
   *
   * ----
   * conversion_details = <
   *     ["source_model"] = <"CEM model xyz <http://location.in.clinicalelementmodels.com>">
   *     ["tool"] = <"cem2adl v6.3.0">
   *     ["time"] = <"2014-11-03T09:05:00">
   * >
   * ----
   */
  conversion_details?: undefined;
  /**
   * Additional non-language-sensitive resource meta-data, as a list of name/value pairs.
   */
  other_details?: undefined;
  /**
   * Details of all parts of resource description that are natural language-dependent, keyed by language code.
   */
  details?: undefined;
}

/**
 * Class providing details of a natural language translation.
 */
export class TRANSLATION_DETAILS {
  /**
   * Language of the translation, coded using ISO 639-1 (2 character) language codes.
   */
  language?: Terminology_code;
  /**
   * Primary translator name and other demographic details.
   */
  author?: undefined;
  /**
   * Internal storage for accreditation
   * @protected
   */
  protected _accreditation?: String;

  /**
   * Accreditation of primary translator or group, usually a national translator's registration or association membership id.
   */
  get accreditation(): string | undefined {
    return this._accreditation?.value;
  }

  /**
   * Gets the String wrapper object for accreditation.
   * Use this to access String methods.
   */
  get $accreditation(): String | undefined {
    return this._accreditation;
  }

  /**
   * Sets accreditation from either a primitive value or String wrapper.
   */
  set accreditation(val: string | String | undefined) {
    if (val === undefined || val === null) {
      this._accreditation = undefined;
    } else if (typeof val === "string") {
      this._accreditation = String.from(val);
    } else {
      this._accreditation = val;
    }
  }

  /**
   * Any other meta-data.
   */
  other_details?: undefined;
  /**
   * Internal storage for version_last_translated
   * @protected
   */
  protected _version_last_translated?: String;

  /**
   * Version of this resource last time it was translated into the language represented by this \`TRANSLATION_DETAILS\` object.
   */
  get version_last_translated(): string | undefined {
    return this._version_last_translated?.value;
  }

  /**
   * Gets the String wrapper object for version_last_translated.
   * Use this to access String methods.
   */
  get $version_last_translated(): String | undefined {
    return this._version_last_translated;
  }

  /**
   * Sets version_last_translated from either a primitive value or String wrapper.
   */
  set version_last_translated(val: string | String | undefined) {
    if (val === undefined || val === null) {
      this._version_last_translated = undefined;
    } else if (typeof val === "string") {
      this._version_last_translated = String.from(val);
    } else {
      this._version_last_translated = val;
    }
  }

  /**
   * Additional contributors to this translation, each listed in the preferred format of the relevant organisation for the artefacts in question. A typical default is \`"name <email>"\` if nothing else is specified.
   */
  other_contributors?: undefined;
}

/**
 * Language-specific detail of resource description. When a resource is translated for use in another language environment, each \`RESOURCE_DESCRIPTION_ITEM\` needs to be copied and translated into the new language.
 */
export class RESOURCE_DESCRIPTION_ITEM {
  /**
   * The localised language in which the items in this description item are written. Coded using ISO 639-1 (2 character) language codes.
   */
  language?: Terminology_code;
  /**
   * Internal storage for purpose
   * @protected
   */
  protected _purpose?: String;

  /**
   * Purpose of the resource.
   */
  get purpose(): string | undefined {
    return this._purpose?.value;
  }

  /**
   * Gets the String wrapper object for purpose.
   * Use this to access String methods.
   */
  get $purpose(): String | undefined {
    return this._purpose;
  }

  /**
   * Sets purpose from either a primitive value or String wrapper.
   */
  set purpose(val: string | String | undefined) {
    if (val === undefined || val === null) {
      this._purpose = undefined;
    } else if (typeof val === "string") {
      this._purpose = String.from(val);
    } else {
      this._purpose = val;
    }
  }

  /**
   * Keywords which characterise this resource, used e.g. for indexing and searching.
   */
  keywords?: undefined;
  /**
   * Internal storage for use
   * @protected
   */
  protected _use?: String;

  /**
   * Description of the uses of the resource, i.e. contexts in which it could be used.
   */
  get use(): string | undefined {
    return this._use?.value;
  }

  /**
   * Gets the String wrapper object for use.
   * Use this to access String methods.
   */
  get $use(): String | undefined {
    return this._use;
  }

  /**
   * Sets use from either a primitive value or String wrapper.
   */
  set use(val: string | String | undefined) {
    if (val === undefined || val === null) {
      this._use = undefined;
    } else if (typeof val === "string") {
      this._use = String.from(val);
    } else {
      this._use = val;
    }
  }

  /**
   * Internal storage for misuse
   * @protected
   */
  protected _misuse?: String;

  /**
   * Description of any misuses of the resource, i.e. contexts in which it should not be used.
   */
  get misuse(): string | undefined {
    return this._misuse?.value;
  }

  /**
   * Gets the String wrapper object for misuse.
   * Use this to access String methods.
   */
  get $misuse(): String | undefined {
    return this._misuse;
  }

  /**
   * Sets misuse from either a primitive value or String wrapper.
   */
  set misuse(val: string | String | undefined) {
    if (val === undefined || val === null) {
      this._misuse = undefined;
    } else if (typeof val === "string") {
      this._misuse = String.from(val);
    } else {
      this._misuse = val;
    }
  }

  /**
   * URIs of original clinical document(s) or description of which resource is a formalisation, in the language of this description item; keyed by meaning.
   */
  original_resource_uri?: undefined;
  /**
   * Additional language-senstive resource metadata, as a list of name/value pairs.
   */
  other_details?: undefined;
}

/**
 * Object representing annotations on an archetype. These can be of various forms, with a documentation form defined so far, which has a multi-level tabular structure [ [ [String value, String key], path key], language key]. Example instance, showing the documentation structure.
 *
 * --------
 *     documentation = <
 *         ["en"] = <
 *            ["/data[id2]"] = <
 *                ["ui"] = <"passthrough">
 *            >
 *            ["/data[id2]/items[id3]"] = <
 *                ["design note"] = <"this is a design note on Statement">
 *                ["requirements note"] = <"this is a requirements note on Statement">
 *                ["medline ref"] = <"this is a medline ref on Statement">
 *            >
 *         >
 *     >
 * --------
 *
 * Other sub-structures might have different keys, e.g.  based on programming languages, UI toolkits etc.
 */
export class RESOURCE_ANNOTATIONS {
  /**
   * Documentary annotations in a multi-level keyed structure.
   */
  documentation?: undefined;
}
